/* eslint-disable react-hooks/exhaustive-deps */
import { tokens } from "../../../theme";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import FilterBar from "./FilterBar";
import PatchBlock from "./PatchBlock";
import EmptyState from "./EmptyState";
import { releaseStyles } from "../styles";
import {
  adaptReleaseView,
  emptyReason,
  matchesQuery,
  matchesSegment,
  segmentCounts,
} from "./releaseViewHelpers";

const SEARCH_DEBOUNCE_MS = 150;

const NO_FILTERS = {
  releases: [],
  patches: [],
  repositories: [],
  segment: "all",
};

const unique = (values) => Array.from(new Set(values));

const isScrollable = (el) => {
  const overflowY = getComputedStyle(el).overflowY;
  return overflowY === "auto" || overflowY === "scroll";
};

// Walk up from the container to the app's own scrollable pane (`.content` in
// App.js) rather than hardcoding that class, so Virtuoso virtualizes against
// the page's real scrollbar instead of owning a second, nested one — the
// filter bar above the list is sticky to that same scroll, and a bounded
// Virtuoso box here would both break that and add a visible inner scrollbar.
const findScrollParent = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body && !isScrollable(node)) {
    node = node.parentElement;
  }
  return node ?? undefined;
};

// Release view: every patch renders as one flat commit table. The response nests
// repositories under patches, but repository is a column here, not a level.
const ReleaseView = ({ getReleasesResp }) => {
  const theme = useTheme();
  const { releaseView } = releaseStyles(tokens(theme.paletteId));

  const [releases, setReleases] = useState([]);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const containerRef = useRef(null);
  const [scrollParent, setScrollParent] = useState(undefined);
  // Layout effect, not a regular one: resolves before the browser paints, so
  // Virtuoso never gets a first frame with no scroll parent (which would
  // otherwise fall back to its own zero-height box for one tick).
  useLayoutEffect(() => {
    setScrollParent(findScrollParent(containerRef.current));
  }, []);

  useEffect(() => {
    if (!Array.isArray(getReleasesResp?.response)) return;
    const adapted = adaptReleaseView(getReleasesResp.response);
    setReleases(adapted);
    const firstWithCommits = adapted
      .flatMap((release) => release.patches)
      .find((patch) => patch.commits.length);
    setExpanded(new Set(firstWithCommits ? [firstWithCommits.key] : []));
  }, [getReleasesResp]);

  useEffect(() => {
    const timer = setTimeout(
      () => setQuery(searchInput.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  const releaseOptions = useMemo(
    () => releases.map((release) => release.label),
    [releases]
  );

  const scopedReleases = useMemo(
    () =>
      filters.releases.length
        ? releases.filter((release) => filters.releases.includes(release.label))
        : releases,
    [releases, filters.releases]
  );

  const patchOptions = useMemo(
    () =>
      unique(
        scopedReleases.flatMap((release) =>
          release.patches.map((patch) => patch.label)
        )
      ),
    [scopedReleases]
  );

  const scopedPatches = useMemo(() => {
    const patches = scopedReleases.flatMap((release) => release.patches);
    return filters.patches.length
      ? patches.filter((patch) => filters.patches.includes(patch.label))
      : patches;
  }, [scopedReleases, filters.patches]);

  const repositoryOptions = useMemo(
    () =>
      unique(
        scopedPatches.flatMap((patch) =>
          patch.commits.map((commit) => commit.repoName)
        )
      )
        .filter(Boolean)
        .sort(),
    [scopedPatches]
  );

  // Repository and search narrow the pool the segment counts are taken from, so the
  // totals on the toggle always describe every patch currently in scope.
  const narrowedPatches = useMemo(
    () =>
      scopedPatches.map((patch) => ({
        ...patch,
        commits: patch.commits.filter(
          (commit) =>
            (!filters.repositories.length ||
              filters.repositories.includes(commit.repoName)) &&
            matchesQuery(commit, query)
        ),
      })),
    [scopedPatches, filters.repositories, query]
  );

  const counts = useMemo(
    () => segmentCounts(narrowedPatches.flatMap((patch) => patch.commits)),
    [narrowedPatches]
  );

  const visiblePatches = useMemo(
    () =>
      narrowedPatches.map((patch) => ({
        ...patch,
        commits: patch.commits.filter((commit) =>
          matchesSegment(commit, filters.segment)
        ),
      })),
    [narrowedPatches, filters.segment]
  );

  const totalCommits = visiblePatches.reduce(
    (total, patch) => total + patch.commits.length,
    0
  );

  const changeFilter = (key, value) =>
    setFilters((prev) => {
      if (key === "releases")
        return { ...prev, releases: value, patches: [], repositories: [] };
      if (key === "patches")
        return { ...prev, patches: value, repositories: [] };
      return { ...prev, [key]: value };
    });

  const clearFilters = () => {
    setFilters(NO_FILTERS);
    setSearchInput("");
    setQuery("");
  };

  const allExpanded =
    visiblePatches.length > 0 &&
    visiblePatches.every((patch) => expanded.has(patch.key));

  const toggleAll = () =>
    setExpanded(
      allExpanded
        ? new Set()
        : new Set(visiblePatches.map((patch) => patch.key))
    );

  const togglePatch = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Box sx={releaseView.container} ref={containerRef}>
      <FilterBar
        releaseOptions={releaseOptions}
        patchOptions={patchOptions}
        repositoryOptions={repositoryOptions}
        filters={filters}
        onFilterChange={changeFilter}
        onClearAll={clearFilters}
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        counts={counts}
        allExpanded={allExpanded}
        onToggleAll={toggleAll}
      />

      {totalCommits ? (
        <Virtuoso
          customScrollParent={scrollParent}
          data={visiblePatches}
          computeItemKey={(index) => visiblePatches[index]?.key}
          itemContent={(_index, patch) => (
            <PatchBlock
              // Virtuoso's own item wrappers sit between these blocks now,
              // so neither the container's flex gap nor the Stack's above
              // (which only spaces direct children, and Virtuoso is its
              // only one) reach between individual blocks anymore — each
              // block owns its own gap here instead. ServiceView (still
              // unvirtualized, still a direct child of that flex gap)
              // doesn't pass this, so it's unaffected.
              sx={{ mb: "10px" }}
              patch={patch}
              open={expanded.has(patch.key)}
              onToggle={() => togglePatch(patch.key)}
            />
          )}
        />
      ) : (
        <EmptyState
          patchCount={scopedPatches.length}
          releases={scopedReleases.map((release) => ({
            label: release.label,
            patchLabels: release.patches
              .filter((patch) => scopedPatches.includes(patch))
              .map((patch) => patch.label),
          }))}
          reason={emptyReason({
            query,
            segment: filters.segment,
            repositories: filters.repositories,
            hasCommits: scopedPatches.some((patch) => patch.commits.length),
          })}
          onClear={clearFilters}
        />
      )}
    </Box>
  );
};

export default ReleaseView;
